import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import API from "../../../Network/API";
import ConfigFormRow from "./props/ConfigFormRow";
import RmsPortConfigRow from "./props/RmsPortConfigRow";
import {
    RMS_OTHER_FIELDS,
    RMS_PORT_ROWS_LEFT,
    RMS_PORT_ROWS_RIGHT,
    RMS_SECTIONS,
} from "./rmsConfigSchema";

export default function RmsConfiguration({ siteId }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState(RMS_SECTIONS[0].id);
    const [site, setSite] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState("");
    const [portOptions, setPortOptions] = useState([]);
    const [form, setForm] = useState({});

    const loadConfiguration = (query, tenantId = null) => {
        setLoading(true);
        const params = tenantId ? { tenant_id: tenantId } : {};
        API.get(`/v1/oauth/rms-configuration/${encodeURIComponent(query)}`, { params })
            .then((response) => {
                const data = response.data.data;
                setSite(data.site);
                setTenants(data.tenants || []);
                setPortOptions(data.port_options || []);
                setSelectedTenantId(String(data.selected_tenant_id || ""));
                setForm(data.form || {});
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "Failed to load RMS configuration");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (siteId) {
            loadConfiguration(siteId);
        }
    }, [siteId]);

    const handleTenantSearch = (event) => {
        event.preventDefault();
        if (!selectedTenantId) {
            toast.error("Please select a tenant");
            return;
        }
        loadConfiguration(siteId, selectedTenantId);
    };

    const handleChange = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = (event) => {
        event.preventDefault();
        if (!site || !selectedTenantId) {
            toast.error("Please select a tenant before saving");
            return;
        }

        setSaving(true);
        const payload = new FormData();
        payload.append("site_id", site.id);
        payload.append("tenant_id", selectedTenantId);

        Object.entries(form).forEach(([key, value]) => {
            payload.append(key, value ?? "");
        });

        API.post("/v1/oauth/rms-configuration/store", payload)
            .then((response) => {
                toast.success(response.data.message || "RMS configuration saved");
                loadConfiguration(siteId, selectedTenantId);
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "Failed to save RMS configuration");
            })
            .finally(() => setSaving(false));
    };

    const currentSection = RMS_SECTIONS.find((section) => section.id === activeSection) || RMS_SECTIONS[0];

    if (loading) {
        return (
            <div className="door-config-panel p-5 text-center">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                <p className="mt-2 mb-0 text-muted">Loading RMS configuration...</p>
            </div>
        );
    }

    return (
        <div className="door-config-panel rms-config-panel">
            <div className="door-config-panel__head">
                <h6 className="mb-1">RMS Configuration</h6>
                <p className="mb-0 text-muted small">Tenant-wise RMS port mapping and parameter configuration.</p>
                {site ? (
                    <div className="door-config-panel__site">
                        <span className="door-config-panel__site-pill"><strong>Site:</strong> {site.site_id}</span>
                        <span className="door-config-panel__site-pill"><strong>Operator:</strong> {site.customer_site_id}</span>
                        <span className="door-config-panel__site-pill"><strong>Name:</strong> {site.site_name}</span>
                    </div>
                ) : null}
            </div>

            <form className="rms-config-toolbar" onSubmit={handleTenantSearch}>
                <label className="rms-config-toolbar__label">Select tenant for configuration</label>
                <div className="rms-config-toolbar__controls">
                    <input
                        type="text"
                        className="form-control"
                        value={siteId || ""}
                        readOnly
                    />
                    <select
                        className="form-select"
                        value={selectedTenantId}
                        onChange={(event) => setSelectedTenantId(event.target.value)}
                        required
                    >
                        <option value="">Select Tenant</option>
                        {tenants.map((tenant) => (
                            <option key={tenant.tenant_id} value={tenant.tenant_id}>
                                {tenant.tenant_name}
                            </option>
                        ))}
                    </select>
                    <button className="btn btn-dark" type="submit">
                        <i className="fa-light fa-magnifying-glass me-1" />
                        Load
                    </button>
                </div>
            </form>

            <div className="door-config-section-tabs">
                {RMS_SECTIONS.map((section) => (
                    <button
                        key={section.id}
                        type="button"
                        className={`door-config-section-tab ${activeSection === section.id ? "active" : ""}`}
                        onClick={() => setActiveSection(section.id)}
                    >
                        <i className={`${section.icon} me-1`} />
                        {section.title}
                    </button>
                ))}
            </div>

            <form onSubmit={handleSave}>
                <div className="door-config-form-body">
                    <h6 className="mb-3">{currentSection.title}</h6>

                    {activeSection === "ports" ? (
                        <>
                            <p className="rms-config-note mb-3">
                                <strong className="text-danger">High</strong> = Normally Open (NO) &amp;{" "}
                                <strong className="text-danger">Low</strong> = Normally Closed (NC)
                            </p>
                            <div className="rms-port-table-head d-none d-md-grid">
                                <span>Alarm</span>
                                <span>Port</span>
                                <span>Activity</span>
                                <span>Source</span>
                            </div>
                            <div className="rms-port-grid">
                                <div className="rms-port-grid__col">
                                    {RMS_PORT_ROWS_LEFT.map((row) => (
                                        <RmsPortConfigRow
                                            key={row.key}
                                            row={row}
                                            form={form}
                                            portOptions={portOptions}
                                            onChange={handleChange}
                                        />
                                    ))}
                                </div>
                                <div className="rms-port-grid__col">
                                    {RMS_PORT_ROWS_RIGHT.map((row) => (
                                        <RmsPortConfigRow
                                            key={row.key}
                                            row={row}
                                            form={form}
                                            portOptions={portOptions}
                                            onChange={handleChange}
                                        />
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="rms-other-fields">
                            {RMS_OTHER_FIELDS.map((field) => (
                                <ConfigFormRow
                                    key={field.name}
                                    field={field}
                                    form={form}
                                    onChange={handleChange}
                                />
                            ))}
                        </div>
                    )}

                    <div className="door-config-actions">
                        <button type="submit" className="btn btn-primary px-4" disabled={saving || !selectedTenantId}>
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fa-light fa-save me-1" />
                                    Save Configuration
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
